/**
 * Typed bridge to @agent-maurice/ui.
 * Components are cast through `any` so React 18/19 @types/react dual-resolution
 * (file: linked package) does not erase DOM prop types at the call site.
 */
import * as React from 'react'
import {
  Badge as UiBadge,
  Button as UiButton,
  Card as UiCard,
  CardContent as UiCardContent,
  CardDescription as UiCardDescription,
  CardFooter as UiCardFooter,
  CardHeader as UiCardHeader,
  CardTitle as UiCardTitle,
  Dialog as UiDialog,
  DialogClose as UiDialogClose,
  DialogContent as UiDialogContent,
  DialogDescription as UiDialogDescription,
  DialogFooter as UiDialogFooter,
  DialogHeader as UiDialogHeader,
  DialogOverlay as UiDialogOverlay,
  DialogPortal as UiDialogPortal,
  DialogTitle as UiDialogTitle,
  DialogTrigger as UiDialogTrigger,
  Input as UiInput,
  Sheet as UiSheet,
  SheetClose as UiSheetClose,
  SheetContent as UiSheetContent,
  SheetDescription as UiSheetDescription,
  SheetFooter as UiSheetFooter,
  SheetHeader as UiSheetHeader,
  SheetOverlay as UiSheetOverlay,
  SheetPortal as UiSheetPortal,
  SheetTitle as UiSheetTitle,
  SheetTrigger as UiSheetTrigger,
  Tabs as UiTabs,
  TabsContent as UiTabsContent,
  TabsList as UiTabsList,
  TabsTrigger as UiTabsTrigger,
  badgeVariants,
  buttonVariants,
} from '@agent-maurice/ui'

export type ButtonProps = React.ComponentPropsWithoutRef<'button'> & {
  variant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'destructive'
    | 'danger'
    | 'outline'
    | 'ghost'
    | 'link'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  asChild?: boolean
}

export type InputProps = React.ComponentPropsWithoutRef<'input'>

export type BadgeProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?:
    | 'default'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'destructive'
    | 'outline'
    | 'violet'
    | 'cyan'
    | 'muted'
}

export const Button = UiButton as unknown as React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement>
>
export const Input = UiInput as unknown as React.ForwardRefExoticComponent<
  InputProps & React.RefAttributes<HTMLInputElement>
>
export const Badge = UiBadge as unknown as React.FC<BadgeProps>

export const Card = UiCard as unknown as React.FC<React.HTMLAttributes<HTMLDivElement>>
export const CardHeader = UiCardHeader as unknown as React.FC<React.HTMLAttributes<HTMLDivElement>>
export const CardFooter = UiCardFooter as unknown as React.FC<React.HTMLAttributes<HTMLDivElement>>
export const CardTitle = UiCardTitle as unknown as React.FC<React.HTMLAttributes<HTMLHeadingElement>>
export const CardDescription = UiCardDescription as unknown as React.FC<
  React.HTMLAttributes<HTMLParagraphElement>
>
export const CardContent = UiCardContent as unknown as React.FC<React.HTMLAttributes<HTMLDivElement>>

export const Dialog = UiDialog as unknown as React.FC<{
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  modal?: boolean
  children?: React.ReactNode
}>
export const DialogPortal = UiDialogPortal as any
export const DialogOverlay = UiDialogOverlay as any
export const DialogClose = UiDialogClose as any
export const DialogTrigger = UiDialogTrigger as any
export const DialogContent = UiDialogContent as any
export const DialogHeader = UiDialogHeader as any
export const DialogFooter = UiDialogFooter as any
export const DialogTitle = UiDialogTitle as any
export const DialogDescription = UiDialogDescription as any

export const Tabs = UiTabs as unknown as React.FC<{
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  orientation?: 'horizontal' | 'vertical'
  dir?: 'ltr' | 'rtl'
  activationMode?: 'automatic' | 'manual'
  children?: React.ReactNode
  className?: string
}>
export const TabsList = UiTabsList as any
export const TabsTrigger = UiTabsTrigger as any
export const TabsContent = UiTabsContent as any

export const Sheet = UiSheet as unknown as React.FC<{
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  modal?: boolean
  children?: React.ReactNode
}>
export const SheetPortal = UiSheetPortal as any
export const SheetOverlay = UiSheetOverlay as any
export const SheetTrigger = UiSheetTrigger as any
export const SheetClose = UiSheetClose as any
export const SheetContent = UiSheetContent as unknown as React.ForwardRefExoticComponent<
  React.ComponentPropsWithoutRef<'div'> & {
    side?: 'top' | 'bottom' | 'left' | 'right'
    closeLabel?: string
    className?: string
    children?: React.ReactNode
  } & React.RefAttributes<HTMLDivElement>
>
export const SheetHeader = UiSheetHeader as unknown as React.FC<
  React.HTMLAttributes<HTMLDivElement>
>
export const SheetFooter = UiSheetFooter as unknown as React.FC<
  React.HTMLAttributes<HTMLDivElement>
>
export const SheetDescription = UiSheetDescription as any
export const SheetTitle = UiSheetTitle as any

export { badgeVariants, buttonVariants }
